import { useState, useEffect, useRef } from "react";
import { db, functions } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { doc, collection, onSnapshot, updateDoc } from "firebase/firestore";

import { useAuth } from "../AuthContext";
import Header from "../components/Header";
import { useDesignVersion } from "../DesignVersionContext";
import AdminV2 from "./AdminV2";
import common from "../style/screens/Common.module.css";
import style from "../style/screens/Admin.module.css";
import ErrMsg from "../ErrMsg";
import { BlockInput, SelectorInput } from "../components/Inputs";
import { BlockButton, ListButton, TextButton } from "../components/Buttons";
import { PartnerType, usePartner } from "../PartnerContext";

interface admin {
    timestamp: number;
    email: string;
    name: string;
    partnerId: string;
    type: string;
    uid: string;
}

const validatePartnerSelection = (type: string, partnerId: string, partners: PartnerType[]) => {
    if (type === "SUPER_ADMIN" && partnerId) return "최고 관리자는 파트너사를 선택할 수 없습니다.";
    if (type === "GROUP_ADMIN" && !partnerId) return "파트너사를 선택해주세요.";
    if (partnerId && !partners.some((p) => p.id === partnerId)) return ErrMsg["auth/partnerId-not-found"];
    return "";
};

const partnerEmptyLabel = (type: string) => {
    if (!type) return "권한 먼저 선택";
    if (type === "GROUP_ADMIN") return "파트너사 선택";
    return "인네임";
};

const PartnerSelector = ({
    type,
    partnerId,
    setPartnerId,
    error,
}: {
    type: string;
    partnerId: string;
    setPartnerId: (value: string) => void;
    error?: string;
}) => {
    const { partner } = usePartner();
    const disabled = !type || type === "SUPER_ADMIN";

    return (
        <SelectorInput
            placeholder={type === "GROUP_ADMIN" ? "파트너사 선택" : "파트너사"}
            value={type === "SUPER_ADMIN" ? "" : partnerId}
            setValue={setPartnerId}
            error={error}
            emptyLabel={partnerEmptyLabel(type)}
            disabled={disabled}
        >
            {partner.map((item) => (
                <option key={item.id} value={item.id}>
                    {item.name} ({item.id})
                </option>
            ))}
        </SelectorInput>
    );
};

const Admin = () => {
    const { version } = useDesignVersion();
    const { userType } = useAuth();

    if (version === "v2") {
        return <AdminV2 />;
    }

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>계정 관리</h4>
                    <p className={common["text--copy"]}>이메일 변경하거나, 비밀번호를 변경할 수 있습니다.</p>

                    <div className={style["container--edit"]}>
                        <p className={style["text--eyebrow"]}>이름 변경하기</p>
                        <ChangeName />
                    </div>

                    <div className={style["container--edit"]}>
                        <p className={style["text--eyebrow"]}>비밀번호 변경하기</p>
                        <ChangePassword />
                    </div>

                    {userType === "SUPER_ADMIN" && <AdminTable />}

                    {/* <GenerateAPIToken /> */}
                </div>
            </div>
        </>
    );
};

const AdminTable = () => {
    const { user } = useAuth();

    const [search, setSearch] = useState<string>("");

    const [createAdmin, setCreateAdmin] = useState<boolean>(false);
    const [editAdmin, setEditAdmin] = useState<boolean>(false);
    const [target, setTarget] = useState<object>({});
    const [admins, setAdmins] = useState<any[]>([]);

    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, "admin"), (snapshot) => {
            const adminLists = snapshot.docs.map((doc) => doc.data()).sort((a, b) => b.timestamp - a.timestamp);
            setAdmins(adminLists);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        document.body.style.overflow = createAdmin || editAdmin ? "hidden" : "auto";
    }, [createAdmin, editAdmin]);

    return (
        <>
            {admins && ( // 관리자가 없는 경우는 없음
                <>
                    <div className={style["container--rows"]}>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>이메일</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>이름</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>파트너사</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>구분</p>
                        <TextButton text="관리자 추가하기" onClick={() => setCreateAdmin(true)} />
                    </div>
                    <input
                        id="search-admin"
                        className={common["input--kiosk-filter"]}
                        type="text"
                        placeholder="이름, 이메일 또는 파트너사 아이디로 검색"
                        onChange={(e) => {
                            setSearch(e.target.value);
                        }}
                    />
                    <div className={style["wrapper--admin-list"]}>
                        {admins
                            .filter((admin) => {
                                return (
                                    search === "" ||
                                    (search &&
                                        (admin.partnerId?.toLowerCase().includes(search.toLowerCase()) ||
                                            admin.name.toLowerCase().includes(search.toLowerCase()) ||
                                            admin.email.toLowerCase().includes(search.toLowerCase())))
                                );
                            })
                            .map((admin, index) => (
                                <div
                                    key={index}
                                    className={[
                                        style["unit--admin-list"],
                                        admin.uid === user.uid && style["unit--admin-list--me"],
                                    ].join(" ")}
                                >
                                    <div className={style["container--rows"]}>
                                        <p className={style["text--eyebrow"]}>{admin.email}</p>
                                        <p className={style["text--eyebrow"]}>{admin.name}</p>
                                        <p className={style["text--eyebrow"]}>
                                            {admin.partnerId ? admin.partnerId : "인네임"}
                                        </p>
                                        <p className={style["text--eyebrow"]}>
                                            {admin.type === "SUPER_ADMIN"
                                                ? "최고 관리자"
                                                : admin.type === "GROUP_ADMIN"
                                                ? "파트너사 최고 관리자"
                                                : "일반 관리자"}
                                        </p>
                                        {admin.uid !== user.uid && (
                                            <TextButton
                                                text="계정 관리하기"
                                                onClick={() => {
                                                    setTarget(admin);
                                                    setEditAdmin(true);
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                    </div>
                </>
            )}

            {createAdmin && <CreateAdmin close={() => setCreateAdmin(false)} />}

            {editAdmin && <EditAdmin admin={target as admin} close={() => setEditAdmin(false)} />}
        </>
    );
};

const ChangeName = () => {
    const { user } = useAuth();

    const [name, setName] = useState<string>("");
    const [nameError, setNameError] = useState<string>("");

    useEffect(() => {
        if (name && name.length < 2) {
            setNameError(ErrMsg["auth/name-invalid"]);
        } else {
            setNameError("");
        }
    }, [name]);

    const [loading, setLoading] = useState<boolean>(false);

    const change = () => {
        if (!name) {
            setNameError(ErrMsg["auth/name-empty"]);
            return;
        }

        setLoading(true);

        return updateDoc(doc(db, "admin", user.uid), {
            name: name,
        })
            .then(() => {
                setLoading(false);
                alert("이름 변경이 완료되었습니다");
                setName("");
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                alert("관리자 정보 수정 중에 에러가 발생했습니다");
                return 0;
            });
    };

    return (
        <div className={style["wrapper--edit"]}>
            <div className={style["unit--edit"]}>
                <BlockInput type="text" placeholder="이름" value={name} setValue={setName} error={nameError} />
            </div>
            <ListButton text="이름 변경하기" loading={loading} onClick={change} />
        </div>
    );
};

const ChangePassword = () => {
    const { passwordChange } = useAuth();

    const [password, setPassword] = useState<string>("");
    const [passwordError, setPasswordError] = useState<string>("");

    useEffect(() => {
        if (password && password.length < 6) {
            setPasswordError(ErrMsg["auth/password-invalid"]);
        } else {
            setPasswordError("");
        }
    }, [password]);

    const [newPassword, setNewPassword] = useState<string>("");
    const [newPasswordError, setNewPasswordError] = useState<string>("");

    useEffect(() => {
        if (newPassword && newPassword.length < 6) {
            setNewPasswordError(ErrMsg["auth/password-invalid"]);
        } else {
            setNewPasswordError("");
        }
    }, [newPassword]);

    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");

    useEffect(() => {
        if (confirmPassword && newPassword !== confirmPassword) {
            setConfirmPasswordError(ErrMsg["auth/password-not-match"]);
        } else {
            setConfirmPasswordError("");
        }
    }, [confirmPassword, newPassword]);

    const [loading, setLoading] = useState<boolean>(false);

    const change = async () => {
        if (!password) {
            setPasswordError(ErrMsg["auth/password-empty"]);
            return;
        } else if (!newPassword) {
            setNewPasswordError(ErrMsg["auth/password-empty"]);
            return;
        } else if (!confirmPassword) {
            setConfirmPasswordError(ErrMsg["auth/password-empty"]);
            return;
        }

        setLoading(true);

        try {
            await passwordChange(password, newPassword);
            alert("비밀번호 변경이 완료되었습니다");
            setLoading(false);
            setPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (err: any) {
            console.error(err);
            setLoading(false);
            switch (err.message) {
                case "auth/wrong-password":
                    setPasswordError(ErrMsg[err.message]);
                    break;
                default:
                    alert("비밀번호 변경 중 에러가 발생했습니다");
                    break;
            }
        }
    };

    return (
        <div className={style["wrapper--edit"]}>
            <div className={style["unit--edit"]}>
                <BlockInput
                    type="password"
                    placeholder="현재 비밀번호"
                    value={password}
                    setValue={setPassword}
                    error={passwordError}
                />

                <BlockInput
                    type="password"
                    placeholder="새로운 비밀번호"
                    value={newPassword}
                    setValue={setNewPassword}
                    error={newPasswordError}
                />

                <BlockInput
                    type="password"
                    placeholder="새로운 비밀번호 확인"
                    value={confirmPassword}
                    setValue={setConfirmPassword}
                    error={confirmPasswordError}
                />
            </div>
            <ListButton text="비밀번호 변경하기" loading={loading} onClick={change} />
        </div>
    );
};

const CreateAdmin = ({ close }: { close: () => void }) => {
    const init = useRef(true);
    const { partner } = usePartner();

    const [name, setName] = useState<string>("");
    const [nameError, setNameError] = useState<string>("");

    useEffect(() => {
        if (name && name.length < 2) {
            setNameError(ErrMsg["auth/name-invalid"]);
        } else {
            setNameError("");
        }
    }, [name]);

    const [type, setType] = useState<string>("");
    const [typeError, setTypeError] = useState<string>("");

    useEffect(() => {
        if (init.current) {
            init.current = false;
            return;
        }

        if (!type) {
            setTypeError(ErrMsg["auth/type-invalid"]);
        } else {
            setTypeError("");
        }
    }, [type]);

    const [partnerId, setPartnerId] = useState<string>("");
    const [partnerIdError, setPartnerIdError] = useState<string>("");

    useEffect(() => {
        const nextPartnerId = type === "SUPER_ADMIN" ? "" : partnerId;
        if (type === "SUPER_ADMIN" && partnerId) {
            setPartnerId("");
        }
        setPartnerIdError(validatePartnerSelection(type, nextPartnerId, partner));
    }, [type, partnerId, partner]);

    const [email, setEmail] = useState<string>("");
    const [emailError, setEmailError] = useState<string>("");

    useEffect(() => {
        if (email && !/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/.test(email)) {
            setEmailError(ErrMsg["auth/email-invalid"]);
        } else {
            setEmailError("");
        }
    }, [email]);

    const [password, setPassword] = useState<string>("");
    const [passwordError, setPasswordError] = useState<string>("");

    useEffect(() => {
        if (password && password.length < 6) {
            setPasswordError(ErrMsg["auth/password-invalid"]);
        } else {
            setPasswordError("");
        }
    }, [password]);

    const [confirm, setConfirm] = useState<string>("");
    const [confirmError, setConfirmError] = useState<string>("");

    useEffect(() => {
        if (confirm && confirm !== password) {
            setConfirmError(ErrMsg["auth/password-not-match"]);
        } else {
            setConfirmError("");
        }
    }, [confirm, password]);

    const [loading, setLoading] = useState<boolean>(false);

    const create = async () => {
        if (nameError || typeError || partnerIdError || emailError || passwordError || confirmError) {
            return;
        }

        const nextPartnerId = type === "SUPER_ADMIN" ? "" : partnerId;
        const nextPartnerIdError = validatePartnerSelection(type, nextPartnerId, partner);

        if (!name) {
            setNameError(ErrMsg["auth/name-empty"]);
            return;
        } else if (!type) {
            setTypeError(ErrMsg["auth/type-invalid"]);
            return;
        } else if (nextPartnerIdError) {
            setPartnerIdError(nextPartnerIdError);
            return;
        } else if (!email) {
            setEmailError(ErrMsg["auth/email-empty"]);
            return;
        } else if (!password) {
            setPasswordError(ErrMsg["auth/password-empty"]);
            return;
        } else if (!confirm) {
            setConfirmError(ErrMsg["auth/password-empty"]);
            return;
        }

        setLoading(true);

        return httpsCallable(
            functions,
            "adminCreate"
        )({
            name: name,
            type: type,
            partnerId: nextPartnerId,
            email: email,
            password: password,
            confirm: confirm,
        })
            .then((result) => {
                setLoading(false);

                const data = result.data as {
                    code?: string;
                    message?: string;
                };

                if (data.code) {
                    hide();
                }
            })
            .catch((err) => {
                console.error(err.message);
                setLoading(false);
                switch (err.message) {
                    case "auth/name-invalid":
                    case "auth/name-weak":
                        setNameError(ErrMsg[err.message]);
                        break;
                    case "auth/type-invalid":
                        setTypeError(ErrMsg[err.message]);
                        break;
                    case "auth/partnerId-invalid":
                    case "auth/partnerId-not-found":
                        setPartnerIdError(ErrMsg[err.message]);
                        break;
                    case "auth/email-invalid":
                    case "auth/email-already-in-use":
                        setEmailError(ErrMsg[err.message]);
                        break;
                    case "auth/password-invalid":
                    case "auth/password-weak":
                        setPasswordError(ErrMsg[err.message]);
                        break;
                    case "auth/password-not-match":
                        setConfirmError(ErrMsg[err.message]);
                        break;
                    case "auth/not-super-admin":
                        alert("최고 관리자만 생성이 가능합니다");
                        break;
                }

                return false;
            });
    };

    const [active, setActive] = useState<boolean>(false);

    useEffect(() => {
        setActive(true);
    }, []);

    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 520);
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <h4 className={[common["text--title"], common["text--title--overlay"]].join(" ")}>계정 추가</h4>

            <div className={[common["unit--overlay"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={common["wrapper--overlay"]}>
                    <h4 className={common["text--overlay"]}>계정 기본 정보</h4>
                    <p className={common["text--overlay--copy"]}>
                        최고 관리자는 인네임 계정으로 생성되고, 파트너사 최고 관리자는 파트너사 선택이 필수입니다.
                    </p>
                    <div className={style["unit--overlay--inputs"]}>
                        <BlockInput type="text" placeholder="이름" value={name} setValue={setName} error={nameError} />

                        <SelectorInput placeholder="구분" value={type} setValue={setType} error={typeError}>
                            <option value="ADMIN">일반 관리자</option>
                            <option value="GROUP_ADMIN">파트너사 최고 관리자</option>
                            <option value="SUPER_ADMIN">최고 관리자</option>
                        </SelectorInput>

                        <PartnerSelector
                            type={type}
                            partnerId={partnerId}
                            setPartnerId={setPartnerId}
                            error={partnerIdError}
                        />

                        <BlockInput
                            type="text"
                            placeholder="이메일"
                            value={email}
                            setValue={setEmail}
                            error={emailError}
                        />

                        <BlockInput
                            type="password"
                            placeholder="비밀번호"
                            value={password}
                            setValue={setPassword}
                            error={passwordError}
                        />

                        <BlockInput
                            type="password"
                            placeholder="비밀번호 확인"
                            value={confirm}
                            setValue={setConfirm}
                            error={confirmError}
                        />
                    </div>
                </div>
                <div className={common["wrapper--overlay--footer"]}>
                    <ListButton type="Default" text="취소" onClick={hide} />

                    <ListButton text="추가하기" loading={loading} onClick={create} />
                </div>
            </div>
        </div>
    );
};

const EditAdmin = ({ admin, close }: { admin: admin; close: () => void }) => {
    const init = useRef(true);
    const { partner } = usePartner();

    const [name, setName] = useState<string>(admin.name);
    const [nameError, setNameError] = useState<string>("");

    useEffect(() => {
        if (name && name.length < 2) {
            setNameError(ErrMsg["auth/name-invalid"]);
        } else {
            setNameError("");
        }
    }, [name]);

    const [partnerId, setPartnerId] = useState<string>(admin.partnerId);
    const [partnerIdError, setPartnerIdError] = useState<string>("");

    const [type, setType] = useState<string>(admin.type);
    const [typeError, setTypeError] = useState<string>("");

    useEffect(() => {
        if (init.current) {
            init.current = false;
            return;
        }

        if (!type) {
            setTypeError(ErrMsg["auth/type-invalid"]);
        } else {
            setTypeError("");
        }
    }, [type]);

    useEffect(() => {
        const nextPartnerId = type === "SUPER_ADMIN" ? "" : partnerId;
        if (type === "SUPER_ADMIN" && partnerId) {
            setPartnerId("");
        }
        setPartnerIdError(validatePartnerSelection(type, nextPartnerId, partner));
    }, [type, partnerId, partner]);

    const [loading, setLoading] = useState<boolean>(false);

    const edit = async () => {
        if (nameError || typeError || partnerIdError) return;

        const nextPartnerId = type === "SUPER_ADMIN" ? "" : partnerId;
        const nextPartnerIdError = validatePartnerSelection(type, nextPartnerId, partner);
        if (!name) {
            setNameError(ErrMsg["auth/name-empty"]);
            return;
        } else if (!type) {
            setTypeError(ErrMsg["auth/type-invalid"]);
            return;
        } else if (nextPartnerIdError) {
            setPartnerIdError(nextPartnerIdError);
            return;
        }

        setLoading(true);

        return updateDoc(doc(db, "admin", admin.uid), {
            name: name,
            type: type,
            partnerId: nextPartnerId,
        })
            .then(() => {
                setLoading(false);
                hide();
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                alert("관리자 정보 수정 중에 에러가 발생했습니다");
                return 0;
            });
    };

    const [deleteBtn, setDeleteBtn] = useState<"Delete" | "DeleteFill">("Delete");
    const [loadingDelete, setLoadingDelete] = useState<boolean>(false);

    const deleteAdmin = () => {
        if (deleteBtn === "Delete") {
            setDeleteBtn("DeleteFill");
            return;
        }

        setLoadingDelete(true);

        return httpsCallable(
            functions,
            "adminDelete"
        )({
            uid: admin.uid,
        })
            .then((result) => {
                setLoadingDelete(false);
                setDeleteBtn("Delete");

                const data = result.data as {
                    code?: string;
                    message?: string;
                };

                if (data.code) {
                    hide();
                }
            })
            .catch((err) => {
                console.error(err);
                setLoadingDelete(false);
                setDeleteBtn("Delete");

                switch (err.message) {
                    case "auth/same-user":
                        alert("자신은 삭제할 수 없습니다");
                        break;
                    default:
                        alert("에러가 발생했습니다");
                        break;
                }

                return;
            });
    };

    const [active, setActive] = useState<boolean>(false);

    useEffect(() => {
        setActive(true);
    }, []);

    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 520);
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <h4 className={[common["text--title"], common["text--title--overlay"]].join(" ")}>계정 수정</h4>

            <div className={[common["unit--overlay"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={common["wrapper--overlay"]}>
                    <h4 className={common["text--overlay"]}>계정 기본 정보</h4>
                    <p className={common["text--overlay--copy"]}>
                        최고 관리자는 인네임 계정으로 저장되고, 파트너사 최고 관리자는 파트너사 선택이 필수입니다.
                    </p>
                    <div className={style["unit--overlay--inputs"]}>
                        <BlockInput type="text" placeholder="이름" value={name} setValue={setName} error={nameError} />
                        <SelectorInput placeholder="구분" value={type} setValue={setType} error={typeError}>
                            <option value="ADMIN">일반 관리자</option>
                            <option value="GROUP_ADMIN">파트너사 최고 관리자</option>
                            <option value="SUPER_ADMIN">최고 관리자</option>
                        </SelectorInput>

                        <PartnerSelector
                            type={type}
                            partnerId={partnerId}
                            setPartnerId={setPartnerId}
                            error={partnerIdError}
                        />
                    </div>

                    <h4 className={common["text--overlay"]}>계정 삭제</h4>
                    <div className={style["unit--overlay--inputs"]}>
                        <BlockButton
                            type={deleteBtn}
                            text={deleteBtn === "Delete" ? "삭제하기" : "삭제 확인하기"}
                            loading={loadingDelete}
                            onClick={deleteAdmin}
                        />
                    </div>
                </div>
                <div className={common["wrapper--overlay--footer"]}>
                    <ListButton type="Default" text="취소" onClick={hide} />

                    <ListButton text="수정하기" loading={loading} onClick={edit} />
                </div>
            </div>
        </div>
    );
};

/* const GenerateAPIToken = () => {
    const [tokenId, setTokenId] = useState<string>("");
    const [tokenIdErr, setTokenIdErr] = useState<string>("");

    useEffect(() => {
        if (!tokenId) {
            setTokenIdErr(ErrMsg["token/id-invalid"]);
        } else {
            setTokenIdErr("");
        }
    }, [tokenId]);

    const [loading, setLoading] = useState<boolean>(false);

    const generate = () => {
        if (!tokenId) {
            setTokenIdErr(ErrMsg["token/id-invalid"]);
            return;
        }

        setLoading(true);

        return httpsCallable(
            functions,
            "generateJwtToken"
        )({
            id: tokenId,
        })
            .then(({ data }: { data: any }) => {
                setLoading(false);
                alert(`토큰 발급 완료: ${data.token}`);
                console.log(data);
                return;
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                alert("에러가 발생했습니다");
                return;
            });
    };

    const [active, setActive] = useState<boolean>(false);

    useEffect(() => {
        setActive(true);
    }, []);

    const hide = () => {
        setActive(false);
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <h4 className={[common["text--title"], common["text--title--overlay"]].join(" ")}>토큰 발급하기</h4>

            <div className={[common["unit--overlay"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={common["wrapper--overlay"]}>
                    <h4 className={common["text--overlay"]}>계정 기본 정보</h4>
                    <div className={style["unit--overlay--inputs"]}>
                        <BlockInput
                            type="text"
                            placeholder="아이디"
                            value={tokenId}
                            setValue={setTokenId}
                            error={tokenIdErr}
                        />
                    </div>
                </div>
                <div className={common["wrapper--overlay--footer"]}>
                    <ListButton type="Default" text="취소" onClick={hide} />

                    <ListButton text="수정하기" loading={loading} onClick={generate} />
                </div>
            </div>
        </div>
    );
}; */

export default Admin;
